import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Box, Flex, ColorVariant, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { AppDispatch, RootState } from 'store'
import {
  getKitchenProduction,
  getKitchenSuggestions,
  getKitchenVariance,
  getKitchenWaste,
} from 'features/food/foodSlice'
import {
  FoodKitchenSupplyMode,
  IFoodKitchenResponse,
  IKitchenProductionPullLineResponse,
  IKitchenProductionWindowResponse,
  IKitchenVarianceLineResponse,
  IKitchenWasteResponse,
} from 'types/api/foodApi.interface'
import { formatDateForDisplay } from 'utils/helpers'

const formatDay = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const daysAgo = (n: number) => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - n)
  return d
}

export const DashboardTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    logisticsKitchens,
    kitchenProduction,
    kitchenSuggestions,
    kitchenVariance,
    kitchenWaste,
  } = useSelector((store: RootState) => store.food)

  const [day, setDay] = useState(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })

  const dayStr = formatDay(day)
  const fromStr = formatDay(daysAgo(7))

  useEffect(() => {
    dispatch(getKitchenProduction({ day: dayStr }))
    dispatch(getKitchenVariance({ from: fromStr, to: dayStr }))
    dispatch(getKitchenWaste({ from: fromStr, to: dayStr }))
  }, [dispatch, dayStr, fromStr])

  useEffect(() => {
    const first = logisticsKitchens.data?.[0]
    if (first) dispatch(getKitchenSuggestions({ kitchenId: first.id }))
  }, [dispatch, logisticsKitchens.data])

  const kitchens = logisticsKitchens.data || []

  const expiringAlert = useMemo(() => {
    const lines = (kitchenSuggestions || [])
      .filter((s) => s.expiringIngredients?.length)
      .flatMap((s) => s.expiringIngredients)
    return [...new Set(lines)]
  }, [kitchenSuggestions])

  const productionByKitchen = useMemo(() => {
    const map: { [id: number]: typeof kitchenProduction } = {}
    ;(kitchenProduction || []).forEach((row) => {
      const id = row.kitchen?.id
      if (!id) return
      if (!map[id]) map[id] = []
      map[id].push(row)
    })
    return map
  }, [kitchenProduction])

  const varianceByKitchen = useMemo(() => {
    const map: { [id: number]: IKitchenVarianceLineResponse[] } = {}
    ;(kitchenVariance || []).forEach((row) => {
      const id = row.kitchen?.id
      if (!id) return
      map[id] = row.lines || []
    })
    return map
  }, [kitchenVariance])

  const wasteByKitchen = useMemo(() => {
    const map: { [id: number]: (typeof kitchenWaste)[0] } = {}
    ;(kitchenWaste || []).forEach((row) => {
      const id = row.kitchen?.id
      if (id) map[id] = row
    })
    return map
  }, [kitchenWaste])

  const pullColumns: IHonestTableColumn<IKitchenProductionPullLineResponse & { id: string }>[] = [
    {
      id: 'ingredient',
      header: 'Ingredient',
      cell: (r) => <BodyText weight='600'>{r.ingredient?.name || '—'}</BodyText>,
    },
    { id: 'required', header: 'Required', cell: (r) => r.required },
    { id: 'available', header: 'Available', cell: (r) => r.available },
    {
      id: 'fefo',
      header: 'Pull from (FEFO)',
      cell: (r) =>
        (r.pullFrom || [])
          .map(
            (b) =>
              `#${b.batchId}${b.expiryDate ? ` exp ${formatDateForDisplay(b.expiryDate)}` : ''} ×${b.quantity}`,
          )
          .join(' · ') || '—',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) =>
        r.shortfall > 0 ? (
          <StatusPill status='danger'>{`Short ${r.shortfall}`}</StatusPill>
        ) : (
          <StatusPill status='success'>OK</StatusPill>
        ),
    },
  ]

  const firstKitchenPull = useMemo(() => {
    const rows = kitchenProduction || []
    const pull = rows.flatMap((w) => w.pullList || [])
    return pull.map((p, idx) => ({ ...p, id: `${p.ingredient?.id || idx}-${idx}` }))
  }, [kitchenProduction])

  return (
    <Flex column gap='1rem' width='100%'>
      <Flex between center width='100%' wrap gap='1rem'>
        <CalendarInput
          nomarg
          labelText='Operational day'
          defaultValue={day}
          onChange={(d: Date) => d && setDay(d)}
        />
      </Flex>

      {!!expiringAlert.length && (
        <Alert
          title='Expiry pressure'
          description={`${expiringAlert.join(' · ')}. See Suggestions for dishes that consume these first.`}
          type={AlertType.WARNING}
          colorVariant={ColorVariant.PRIMARY}
        />
      )}

      {!kitchens.length && <OtherText grey>No kitchens configured yet — add them under Food → Configuration.</OtherText>}

      <Flex gap='0.875rem' width='100%' wrap>
        {kitchens.map((kitchen) => (
          <KitchenCard
            key={kitchen.id}
            kitchen={kitchen}
            production={productionByKitchen[kitchen.id] || []}
            varianceLines={varianceByKitchen[kitchen.id] || []}
            waste={wasteByKitchen[kitchen.id]}
          />
        ))}
      </Flex>

      <Container>
        <ContainerHeader>
          <TitleText weight='700'>
            Production pull list — {formatDateForDisplay(dayStr)}
          </TitleText>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable
            columns={pullColumns}
            data={firstKitchenPull}
            keyField='id'
            emptyMessage='No pull list for this day'
          />
        </ContainerContent>
      </Container>
    </Flex>
  )
}

const KitchenCard = ({
  kitchen,
  production,
  varianceLines,
  waste,
}: {
  kitchen: IFoodKitchenResponse
  production: IKitchenProductionWindowResponse[]
  varianceLines: IKitchenVarianceLineResponse[]
  waste?: IKitchenWasteResponse
}) => {
  const ordered = production.reduce((s, w) => {
    if (w.reconciliation?.ordered != null) return s + w.reconciliation.ordered
    return s + (w.dishes || []).reduce((a, d) => a + (d.portions || 0), 0)
  }, 0)
  const served = production.reduce((s, w) => s + (w.reconciliation?.served || 0), 0)
  const avgVariance =
    varianceLines.length > 0
      ? (
          varianceLines.reduce((s, l) => s + (l.variancePercent || 0), 0) / varianceLines.length
        ).toFixed(1)
      : '—'

  const isClient = kitchen.supplyMode === FoodKitchenSupplyMode.ClientSupplied

  return (
    <Box width='100%'>
      <Flex between center mb='12'>
        <BodyText weight='700'>{kitchen.name}</BodyText>
        <StatusPill status={isClient ? 'success' : 'info'}>
          {isClient ? 'Client-supplied' : 'Contractor-supplied'}
        </StatusPill>
      </Flex>
      <Flex gap='0.5rem' mb='12' width='100%'>
        <MetricCard value={ordered} label='Portions planned' accentColor={colors.orange} />
        <MetricCard value={served || waste?.served || 0} label='Served' accentColor={colors.orange} />
        <MetricCard
          value={isClient ? `${avgVariance}%` : waste?.plateWaste ?? 0}
          label={isClient ? 'Avg variance' : 'Plate waste'}
          accentColor={colors.orange}
        />
      </Flex>
      <OtherText grey>
        Soft capacity {kitchen.softCapacity}
        {(kitchen.dietaryTagCapabilities || []).length
          ? ` · ${(kitchen.dietaryTagCapabilities || []).map((t) => t.code).join(', ')}`
          : ''}
        {kitchen.storeLocation ? ` · Store: ${kitchen.storeLocation.name}` : ''}
      </OtherText>
    </Box>
  )
}
